/**
 * Chat attachment cards: report artifact, confirmation (pending action), creative approval (approval_request /
 * creative), script review (video shot plan awaiting approval), recommendation, table, media, context.
 * Question/answers cards live in QuestionCard.tsx.
 */
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, CircleAlert, Clapperboard, FileText, Film, Paperclip, ShieldAlert, Sparkles, SquarePen } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { ChatAttachment } from "@/api/chat";
import { threadKey } from "@/api/chat-hooks";
import { ApiError, errorMessage } from "@/api/client";
import { useRunTool, useTool, useToolMutation } from "@/api/hooks";
import { pickObject, type Creative, type CreativeAsset, type Recommendation } from "@/api/types";
import { cn } from "@/lib/cn";
import { FlowCardSlot } from "@/lib/flows";
import { humanize, relative } from "@/lib/format";
import { Pill, type Tone } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Feedback";
import { Textarea } from "../ui/Form";
import { Lightbox, type LightboxItem } from "../ui/Lightbox";
import { ApprovalActions, AspectPair, CreativeStatusPill } from "../creative/CreativeBits";
import { CreativeDetailModal } from "../creative/CreativeDetailModal";
import { RecommendationCard } from "../recommendations/RecommendationCard";
import { Markdown } from "./Markdown";
import { RecommendationRows } from "./report/ReportBody";
import { normalizeTable, ReportTable } from "./report/ReportTable";
import { isVideoUrl, obj, prettyJson, str } from "./util";
import { safePreviewSrc } from "@/lib/safe-url";
import { editorPath } from "@/pages/editor/paths";

export interface AttachmentCtx {
  threadId: string | null;
  brandId: string | null;
  compact?: boolean;
  activeArtifactId?: string | null;
  onOpenArtifact: (id: string) => void;
  /** Flows open in the same right pane as reports (lead's FlowPanelSlot). */
  onOpenFlow?: (f: { flowId: string; title?: string; brandId?: string | null }) => void;
}

export const MEDIA_KINDS = new Set(["image", "video", "asset", "upload", "generated", "creation", "inspiration"]);

export function isMediaAttachment(a: ChatAttachment): boolean {
  if (MEDIA_KINDS.has(a.kind)) return true;
  // A "creative" attachment the user attached (only id + url) renders as media too.
  return a.kind === "creative" && !!a.url && !obj(a.data)?.assets;
}

/** Cards that stand on their own (not media thumbnails). */
export function AttachmentCard({ att, ctx }: { att: ChatAttachment; ctx: AttachmentCtx }) {
  const data = obj(att.data) ?? {};
  switch (att.kind) {
    case "artifact":
      return <ArtifactCard att={att} ctx={ctx} />;
    case "flow": {
      const flowId = str(data.flowId) ?? att.id;
      if (!flowId) return null;
      const brandId = str(data.brandId) ?? ctx.brandId;
      const title = str(data.title) ?? str(data.name);
      return (
        <div className="my-2">
          <FlowCardSlot flowId={flowId} brandId={brandId} title={title} status={str(data.status)} onOpen={ctx.onOpenFlow ? () => ctx.onOpenFlow!({ flowId, title, brandId }) : undefined} />
        </div>
      );
    }
    case "confirmation":
      return <ConfirmationCard att={att} ctx={ctx} />;
    case "script_review":
      return <ScriptReviewCard att={att} ctx={ctx} />;
    case "approval_request":
    case "creative": {
      if (data.status === "script_review") return <ScriptReviewCard att={att} ctx={ctx} />;
      const list = Array.isArray(data.creatives) ? (data.creatives as unknown[]) : null;
      if (list?.length) {
        return (
          <div className="flex flex-col gap-3">
            {list.map((c, i) => (
              <CreativeApprovalCard key={i} att={{ kind: att.kind, data: obj(c) ?? {} }} ctx={ctx} />
            ))}
          </div>
        );
      }
      const ids = Array.isArray(data.creativeIds) ? (data.creativeIds as unknown[]).filter((x): x is string => typeof x === "string") : null;
      if (ids?.length) {
        return (
          <div className="flex flex-col gap-3">
            {ids.map((id) => (
              <CreativeApprovalCard key={id} att={{ kind: att.kind, id }} ctx={ctx} />
            ))}
          </div>
        );
      }
      return <CreativeApprovalCard att={att} ctx={ctx} />;
    }
    case "recommendation":
      return <RecommendationAttachment att={att} ctx={ctx} />;
    case "table": {
      const t = normalizeTable(data);
      return t ? <ReportTable {...t} bare className="my-3" pageSize={10} /> : null;
    }
    case "context":
      return <ContextChip data={data} />;
    default:
      return null;
  }
}

// ─── Report artifact card ──────────────────────────────────────────────────

function ArtifactCard({ att, ctx }: { att: ChatAttachment; ctx: AttachmentCtx }) {
  const d = obj(att.data) ?? {};
  const id = str(d.artifactId) ?? att.id;
  if (!id) return null;
  const title = str(d.title) ?? "Report";
  const rev = typeof d.revision === "number" ? d.revision : null;
  const active = ctx.activeArtifactId === id;
  return (
    <button
      type="button"
      onClick={() => ctx.onOpenArtifact(id)}
      className={cn(
        "group my-2 flex w-full items-center gap-3 rounded-2xl border bg-surface px-4 py-3.5 text-left transition-colors hover:bg-[#232326]",
        active ? "border-lavender shadow-[0_0_0_3px_rgba(86,67,225,0.25)]" : "border-primary/90",
      )}
      aria-label={`Open report: ${title}`}
    >
      <FileText className="size-4.5 shrink-0 text-fg/70" strokeWidth={1.75} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14.5px] text-fg">{title}</span>
        <span className="mt-0.5 block text-[12px] text-muted">
          Report{rev && rev > 1 ? ` · Revision ${rev}` : ""}
          {str(d.publishedAt) ? ` · ${relative(str(d.publishedAt))}` : ""}
        </span>
      </span>
      <ChevronRight className="size-4.5 shrink-0 text-muted transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

// ─── Confirmation card (agent-queued pending action) ───────────────────────

const DECISION: Record<string, { tone: Tone; label: string }> = {
  approved: { tone: "success", label: "Approved & ran" },
  executed: { tone: "success", label: "Approved & ran" },
  rejected: { tone: "danger", label: "Rejected" },
  failed: { tone: "danger", label: "Failed" },
  expired: { tone: "muted", label: "Expired" },
};

function ConfirmationCard({ att, ctx }: { att: ChatAttachment; ctx: AttachmentCtx }) {
  const qc = useQueryClient();
  const d = obj(att.data) ?? {};
  const pendingActionId = str(d.pendingActionId) ?? att.id ?? "";
  const [local, setLocal] = useState<{ status: string; result?: unknown } | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const [details, setDetails] = useState(false);
  const refresh = () => {
    if (ctx.threadId) void qc.invalidateQueries({ queryKey: threadKey(ctx.threadId) });
  };
  const approve = useToolMutation<Record<string, unknown>>("pending.approve", {
    label: "Approve & run",
    success: "Approved — running now",
    onSuccess: (r) => {
      setLocal({ status: "approved", result: r?.result ?? r });
      refresh();
    },
  });
  const reject = useToolMutation("pending.reject", {
    success: "Rejected — nothing was run",
    onSuccess: () => {
      setLocal({ status: "rejected" });
      setRejecting(false);
      refresh();
    },
  });
  const expired = typeof d.expiresAt === "string" && Date.parse(d.expiresAt) < Date.now();
  const status = local?.status ?? str(d.status) ?? "pending";
  const decided = status !== "pending" ? status : expired ? "expired" : null;
  const tool = str(d.tool) ?? "action";
  const result = local?.result ?? d.result;
  return (
    <div className={cn("my-3 overflow-hidden rounded-2xl border bg-surface", decided ? "border-line" : "border-amber/40")}>
      <div className="flex items-start gap-3 px-4 pb-3 pt-4">
        <span className={cn("grid size-8 shrink-0 place-items-center rounded-full", decided ? "bg-elevated text-muted" : "bg-amber-soft text-amber")}>
          <ShieldAlert className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-semibold">{decided ? "Action decided" : "Needs your approval"}</span>
            <span className="rounded-md bg-elevated px-1.5 py-0.5 font-mono text-[11px] text-muted">{tool}</span>
            {decided && (
              <Pill tone={DECISION[decided]?.tone ?? "muted"} size="xs" dot>
                {DECISION[decided]?.label ?? humanize(decided)}
              </Pill>
            )}
          </div>
          <p className="mt-1.5 text-[14px] leading-relaxed text-fg/90">{str(d.summary) ?? "Mimic wants to run an action that needs your OK."}</p>
          {typeof d.decidedAt === "string" && <p className="mt-1 text-[11.5px] text-subtle">Decided {relative(d.decidedAt)}</p>}
          {!decided && typeof d.expiresAt === "string" && <p className="mt-1 text-[11.5px] text-subtle">Expires {relative(d.expiresAt)}</p>}
        </div>
      </div>
      <div className="px-4 pb-3">
        <button type="button" onClick={() => setDetails((x) => !x)} className="inline-flex items-center gap-1 text-[12px] text-muted hover:text-fg" aria-expanded={details}>
          <ChevronDown className={cn("size-3.5 transition-transform", details && "rotate-180")} />
          {details ? "Hide details" : "Details"}
        </button>
        {details && (
          <div className="mt-2 grid gap-2">
            <pre className="max-h-56 overflow-auto rounded-xl border border-line bg-surface-3 p-3 font-mono text-[11.5px] text-fg/80 scrollbar-thin">{prettyJson(d.input ?? {})}</pre>
            {result !== undefined && result !== null && (
              <pre className="max-h-56 overflow-auto rounded-xl border border-line bg-surface-3 p-3 font-mono text-[11.5px] text-fg/80 scrollbar-thin">{prettyJson(result)}</pre>
            )}
          </div>
        )}
      </div>
      {!decided && (
        <div className="border-t border-line bg-surface-2/50 px-4 py-3">
          {rejecting ? (
            <div className="flex flex-col gap-2">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional: tell Mimic why (it learns from this)" autoFocus />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setRejecting(false)}>
                  Cancel
                </Button>
                <Button size="sm" variant="danger" loading={reject.isPending} onClick={() => reject.mutate({ pendingActionId, ...(note.trim() ? { note: note.trim() } : {}), ...(ctx.threadId ? { threadId: ctx.threadId } : {}) })}>
                  Reject
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setRejecting(true)} disabled={approve.isPending}>
                Reject
              </Button>
              <Button size="sm" variant="primary" loading={approve.isPending} onClick={() => approve.mutate({ pendingActionId, ...(ctx.threadId ? { threadId: ctx.threadId } : {}) })}>
                Approve & run
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Creative approval card ────────────────────────────────────────────────

interface CardCreative {
  creative: Creative;
  hook?: string;
  warnings: string[];
  brandId: string | null;
}

/** creatives package ApprovalCard / Creative / creatives.get result → one shape. */
function toCardCreative(raw: Record<string, unknown>, fallbackBrand: string | null): CardCreative | null {
  const c = obj(raw.creative) ?? raw;
  const id = str(c.creativeId) ?? str(c.id);
  if (!id) return null;
  const meta = obj(c.meta) ?? {};
  const assetsRaw = (Array.isArray(c.assets) ? c.assets : Array.isArray(raw.assets) ? raw.assets : []) as Record<string, unknown>[];
  const qa = (Array.isArray(c.qa) ? c.qa : []) as Record<string, unknown>[];
  const assets: CreativeAsset[] = assetsRaw
    .filter((a) => a && a.status !== "superseded")
    .map((a) => {
      const q = qa.find((x) => x.aspect === a.aspect);
      return {
        id: str(a.assetId) ?? str(a.id) ?? `${id}-${String(a.aspect)}`,
        aspect: String(a.aspect ?? "1:1"),
        mediaType: a.mediaType === "video" ? "video" : "image",
        url: str(a.url) ?? null,
        thumbnailUrl: str(a.thumbnailUrl) ?? null,
        status: str(a.status),
        qaStatus: (str(a.qaStatus) as CreativeAsset["qaStatus"]) ?? undefined,
        qaReport: q ? ({ verdict: q.verdict === "fail" ? "fail" : "pass", issues: Array.isArray(q.issues) ? q.issues : [], transcribedText: [], expectedText: [] } as CreativeAsset["qaReport"]) : (obj(a.qaReport) as CreativeAsset["qaReport"]) ?? null,
      };
    });
  return {
    creative: {
      id,
      name: str(c.name) ?? "Creative",
      kind: c.kind === "video" ? "video" : "image",
      status: str(c.status) ?? "pending_approval",
      format: str(c.format),
      emotionalTrigger: str(meta.emotionalTrigger) ?? str(c.emotionalTrigger),
      awarenessStage: str(meta.awarenessStage) ?? str(c.awarenessStage),
      assets,
    },
    hook: str(meta.hook) ?? str(c.hook),
    warnings: Array.isArray(c.warnings) ? (c.warnings as unknown[]).filter((w): w is string => typeof w === "string") : [],
    brandId: str(c.brandId) ?? fallbackBrand,
  };
}

function CreativeApprovalCard({ att, ctx }: { att: ChatAttachment; ctx: AttachmentCtx }) {
  const d = obj(att.data) ?? {};
  const inline = useMemo(() => (Array.isArray(d.assets) || Array.isArray(obj(d.creative)?.assets) ? toCardCreative(d, ctx.brandId) : null), [d, ctx.brandId]);
  const creativeId = inline?.creative.id ?? str(d.creativeId) ?? att.id ?? null;
  const brandId = inline?.brandId ?? str(d.brandId) ?? ctx.brandId;
  const fetched = useTool<unknown>("creatives.get", !inline && creativeId && brandId ? { brandId, creativeId } : null, { staleTime: 30_000 });
  const card = inline ?? (fetched.data ? toCardCreative(pickObject<Record<string, unknown>>(fetched.data, "approvalCard") ?? (fetched.data as Record<string, unknown>), brandId) : null);
  const [decision, setDecision] = useState<"approved" | "rejected" | null>(null);
  const [open, setOpen] = useState(false);
  if (!card) {
    if (fetched.isLoading) return <Skeleton className="my-3 h-64 w-full rounded-2xl" />;
    if (fetched.error) return <div className="my-3 rounded-xl border border-line px-4 py-3 text-[13px] text-muted">Couldn't load this creative: {errorMessage(fetched.error)}</div>;
    return null;
  }
  const status = decision ?? card.creative.status;
  const needsDecision = !decision && (card.creative.status === "pending_approval" || card.creative.status === "qa_failed");
  return (
    <div className="my-3 rounded-2xl border border-line bg-surface p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {card.creative.kind === "video" ? <Film className="size-4 text-muted" /> : <Sparkles className="size-4 text-muted" />}
            <span className="truncate text-[14.5px] font-semibold">{card.creative.name}</span>
          </div>
          {card.hook && <p className="mt-1 text-[13px] italic text-fg/75">“{card.hook}”</p>}
        </div>
        <CreativeStatusPill status={status} />
      </div>
      <AspectPair creative={card.creative} height={ctx.compact ? 170 : 220} />
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {card.creative.emotionalTrigger && <Pill tone="primary" size="xs">{humanize(card.creative.emotionalTrigger)}</Pill>}
        {card.creative.awarenessStage && <Pill tone="info" size="xs">{humanize(card.creative.awarenessStage)}</Pill>}
        {card.creative.format && <Pill tone="neutral" size="xs">{humanize(card.creative.format)}</Pill>}
      </div>
      {card.warnings.length > 0 && (
        <ul className="mt-3 space-y-1 rounded-xl bg-amber-soft/60 px-3 py-2 text-[12.5px] text-amber">
          {card.warnings.map((w, i) => (
            <li key={i} className="flex gap-1.5">
              <CircleAlert className="mt-0.5 size-3.5 shrink-0" />
              {w}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3.5 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3">
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => setOpen(true)} className="text-[13px] font-medium text-link hover:text-lavender">
            Open details
          </button>
          {brandId && card.creative.kind !== "video" && (card.creative.assets ?? []).some((a) => a.mediaType === "image" && a.url) && (
            <Link to={editorPath(brandId, card.creative.id)} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-link hover:text-lavender" title="Keep editing it by talking to Mimic, with filters and adjustments">
              <SquarePen className="size-3.5" strokeWidth={1.75} />
              Open in editor
            </Link>
          )}
        </div>
        {needsDecision && brandId ? <ApprovalActions brandId={brandId} creativeId={card.creative.id} onDone={setDecision} /> : null}
      </div>
      {brandId && <CreativeDetailModal brandId={brandId} creativeId={card.creative.id} open={open} onClose={() => setOpen(false)} />}
    </div>
  );
}

// ─── Script review (video shot plan gate) ──────────────────────────────────

function shotRows(d: Record<string, unknown>): { time: string; visual: string; line: string }[] {
  const script = obj(d.script) ?? obj(obj(d.creative)?.script) ?? obj(d.plan) ?? {};
  const shots = (Array.isArray(script.table) ? script.table : Array.isArray(script.shots) ? script.shots : Array.isArray(d.shots) ? d.shots : Array.isArray(script.scenes) ? script.scenes : []) as unknown[];
  return shots
    .map((s) => obj(s))
    .filter((s): s is Record<string, unknown> => !!s)
    .map((s) => {
      const start = s.start ?? s.startSec ?? s.from;
      const end = s.end ?? s.endSec ?? s.to;
      const time = str(s.time) ?? str(s.timing) ?? (start !== undefined && end !== undefined ? `${start}—${end}s` : "");
      return { time, visual: str(s.visual) ?? str(s.scene) ?? str(s.description) ?? "", line: str(s.spokenLine) ?? str(s.line) ?? str(s.spoken) ?? str(s.voiceover) ?? str(s.dialogue) ?? str(s.text) ?? "—" };
    });
}

export function ScriptReviewCard({ att, ctx, showTable = true }: { att: ChatAttachment; ctx: AttachmentCtx; showTable?: boolean }) {
  const runTool = useRunTool();
  const d = obj(att.data) ?? {};
  const creativeId = str(d.creativeId) ?? str(obj(d.creative)?.id) ?? str(d.id) ?? att.id ?? "";
  const brandId = str(d.brandId) ?? ctx.brandId;
  const name = str(d.name) ?? str(obj(d.creative)?.name) ?? str(obj(d.plan)?.headline) ?? "Video script";
  const rows = showTable ? shotRows(d) : [];
  const [mode, setMode] = useState<"idle" | "changes">("idle");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<null | "approve" | "changes">(null);
  const [state, setState] = useState<null | "approved" | "changes_requested">(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (kind: "approve" | "changes") => {
    if (!brandId || !creativeId) return;
    setBusy(kind);
    setError(null);
    try {
      const r =
        kind === "approve"
          ? await runTool("creatives.approve_script", { brandId, creativeId }, { label: "Approve script & generate", silentError: true })
          : await runTool("creatives.revise_script", { brandId, creativeId, notes: note.trim() }, { label: "Revise the script", silentError: true });
      if (r !== undefined) {
        setState(kind === "approve" ? "approved" : "changes_requested");
        setMode("idle");
      }
    } catch (e) {
      setError(e instanceof ApiError && e.isUnknownTool ? "Script review isn't available on the server yet — reply in the chat instead (e.g. “approve the script” or what to change)." : errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  const decided = state === "approved" ? "approved" : d.status === "script_approved" || d.status === "generating" || d.status === "draft" ? "approved" : null;
  return (
    <div className="my-3 overflow-hidden rounded-2xl border border-primary/50 bg-surface">
      <div className="flex items-start gap-3 px-4 pt-4">
        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary-soft text-lavender">
          <Clapperboard className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold">Review script</div>
          <div className="truncate text-[12.5px] text-muted">{name} · nothing is generated until you approve</div>
          {state === "changes_requested" && <div className="mt-1 text-[12.5px] text-muted">Revision requested — the updated plan appears in the chat.</div>}
        </div>
        {(decided || state) && (
          <Pill tone={decided === "approved" ? "success" : "amber"} size="xs" dot>
            {decided === "approved" ? "Approved — generating" : "Changes requested"}
          </Pill>
        )}
      </div>
      {rows.length > 0 && (
        <div className="px-4 pt-3">
          <ShotTable rows={rows} />
        </div>
      )}
      {str(d.summary) && <Markdown text={str(d.summary)!} className="px-4 pt-3 text-[13.5px] text-fg/85" />}
      {error && <p className="mx-4 mt-3 rounded-lg bg-danger-soft px-3 py-2 text-[12.5px] text-danger">{error}</p>}
      {!decided && (
        <div className="mt-3 border-t border-line bg-surface-2/50 px-4 py-3">
          {mode === "changes" ? (
            <div className="flex flex-col gap-2">
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What should change? e.g. “shorter hook, swap the 13–16s insert for the editor timeline”" autoFocus />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setMode("idle")}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" disabled={note.trim().length < 3} loading={busy === "changes"} onClick={() => void run("changes")}>
                  Send changes
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setMode("changes")} disabled={!!busy || !brandId}>
                Request changes
              </Button>
              <Button size="sm" variant="primary" loading={busy === "approve"} disabled={!brandId || !creativeId} onClick={() => void run("approve")}>
                Approve & generate
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ShotTable({ rows }: { rows: { time: string; visual: string; line: string }[] }) {
  return (
    <div className="overflow-x-auto rounded-xl bg-[#232325] scrollbar-thin">
      <table className="w-full min-w-[520px] border-collapse text-[13.5px]">
        <thead>
          <tr className="bg-[#2a2a2c] text-left">
            <th className="w-16 px-4 py-2.5 font-medium text-fg/90">Time</th>
            <th className="px-4 py-2.5 font-medium text-fg/90">Visual</th>
            <th className="px-4 py-2.5 font-medium text-fg/90">Proposed spoken line</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="align-middle">
              <td className="px-4 py-3 text-muted tnum">{r.time}</td>
              <td className="px-4 py-3 text-fg/90">{r.visual}</td>
              <td className="px-4 py-3 text-fg/90">{r.line}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Recommendation ────────────────────────────────────────────────────────

function RecommendationAttachment({ att, ctx }: { att: ChatAttachment; ctx: AttachmentCtx }) {
  const d = obj(att.data) ?? {};
  if (str(d.target) && str(d.action)) {
    const tool = obj(d.tool);
    return (
      <div className="my-3">
        <RecommendationRows
          item={{
            target: str(d.target)!,
            action: str(d.action)!,
            success: str(d.success) ?? "",
            guardrail: str(d.guardrail) ?? "",
            reviewDate: str(d.reviewDate) ?? "",
            tool: tool && str(tool.name) ? { name: str(tool.name)!, input: obj(tool.input) ?? {} } : undefined,
          }}
          brandId={ctx.brandId}
        />
      </div>
    );
  }
  const rec = { ...(d as unknown as Recommendation), id: str(d.id) ?? att.id ?? "", title: str(d.title) ?? "Recommendation", kind: str(d.kind) ?? "other", status: str(d.status) ?? "open" };
  const brandId = str(d.brandId) ?? ctx.brandId;
  if (!brandId || !rec.id) return null;
  return <RecommendationCard rec={rec} brandId={brandId} variant="full" className="my-3" />;
}

// ─── Context + media ───────────────────────────────────────────────────────

function ContextChip({ data }: { data: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);
  const widget = obj(data.widget);
  const label = str(widget?.title) ?? str(data.title) ?? str(data.page) ?? (data.dashboardId ? "Dashboard widget" : "Page context");
  return (
    <div className="ml-auto w-fit max-w-[88%]">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3 py-1 text-[12px] text-muted hover:text-fg">
        <Paperclip className="size-3" />
        Context: {label}
        <ChevronDown className={cn("size-3 transition-transform", open && "rotate-180")} />
      </button>
      {open && <pre className="mt-1.5 max-h-56 overflow-auto rounded-xl border border-line bg-surface-3 p-3 font-mono text-[11px] text-fg/75 scrollbar-thin">{prettyJson(data)}</pre>}
    </div>
  );
}

/** Thumbnails for attached / produced media; click → Lightbox with ‹ ›. */
export function MediaGrid({ items, size = "md", className }: { items: ChatAttachment[]; size?: "sm" | "md"; className?: string }) {
  const media = items.map((a) => {
    const d = obj(a.data) ?? {};
    const url = a.url ?? str(d.url) ?? str(d.thumbnailUrl) ?? null;
    const mediaType = a.kind === "video" || d.mediaType === "video" || isVideoUrl(url) ? "video" : "image";
    return { url, mediaType, caption: str(d.name) ?? str(d.title) ?? undefined } as LightboxItem & { mediaType: string };
  });
  const [zoom, setZoom] = useState<number | null>(null);
  if (!media.length) return null;
  const dims = size === "sm" ? "size-[100px]" : media.length === 1 ? "max-h-[320px] max-w-[min(100%,320px)]" : "size-[150px]";
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {media.map((m, i) => (
        <button key={i} type="button" onClick={() => setZoom(i)} className={cn("relative overflow-hidden rounded-xl border border-line bg-[#151517]", dims)} aria-label={`View ${m.caption ?? m.mediaType}`}>
          {m.url ? (
            m.mediaType === "video" && isVideoUrl(m.url) ? (
              <video src={safePreviewSrc(m.url)} muted playsInline preload="metadata" className={cn("object-cover", size === "sm" || media.length > 1 ? "size-full" : "max-h-[320px]")} />
            ) : (
              <img src={safePreviewSrc(m.url)} alt={m.caption ?? ""} loading="lazy" className={cn("object-cover", size === "sm" || media.length > 1 ? "size-full" : "max-h-[320px] w-auto")} />
            )
          ) : (
            <span className="grid size-[100px] place-items-center text-subtle">{m.mediaType === "video" ? <Film className="size-5" /> : <Paperclip className="size-5" />}</span>
          )}
          {m.mediaType === "video" && (
            <span className="absolute bottom-1.5 left-1.5 grid size-6 place-items-center rounded-full bg-black/60 text-white">
              <Film className="size-3" />
            </span>
          )}
        </button>
      ))}
      {zoom !== null && <Lightbox items={media} index={zoom} onIndex={setZoom} onClose={() => setZoom(null)} />}
    </div>
  );
}
